import React, { useState } from 'react';
import styled from 'styled-components';
import { sendPasswordResetEmail, fetchSignInMethodsForEmail } from 'firebase/auth';
import { auth } from '../firebase/config';

interface IdPasswordFindModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
`;

const ModalWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100vw;
  min-height: 100vh;
  padding: 80px 0 160px 0;
  box-sizing: border-box;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ModalContent = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  padding: 32px;
  max-width: 400px;
  width: 90%;
  text-align: center;
  border: var(--modal-border);
  box-shadow: var(--modal-shadow-large);
  margin: auto;
`;

const Title = styled.h2`
  color: var(--color-text-primary);
  margin-bottom: 24px;
  font-size: 1.5rem;
  font-weight: bold;
`;

const TabContainer = styled.div`
  display: flex;
  margin-bottom: 24px;
  border-bottom: 1px solid var(--color-border);
`;

const Tab = styled.button<{ isActive: boolean }>`
  flex: 1;
  padding: 12px;
  border: none;
  background: none;
  color: ${props => props.isActive ? 'var(--color-accent-strong)' : '#666'};
  
  /* 다크모드: 비선택 탭 텍스트를 조금 더 밝게 */
  :root:not([data-theme='light']) & {
    color: ${props => props.isActive ? 'var(--color-accent-strong)' : '#777777'};
  }
  font-size: 1rem;
  font-weight: ${props => props.isActive ? 'bold' : 'normal'};
  cursor: pointer;
  border-bottom: 2px solid ${props => props.isActive ? 'var(--color-accent-strong)' : 'transparent'};
  transition: all 0.3s ease;
  
  &:hover {
    color: var(--color-accent-strong);
  }
  
  &:focus {
    outline: none;
  }
  
  /* 다크모드: 선택한 탭의 텍스트를 흰색으로, 비선택 탭은 조금 더 밝게 */
  :root:not([data-theme='light']) & {
    color: ${props => props.isActive ? '#ffffff' : '#777777'};
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: left;
`;

const Label = styled.label`
  color: var(--color-text-primary);
  font-weight: bold;
  font-size: 0.9rem;
`;

const Input = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 12px;
  border: 2px solid var(--color-border);
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s ease;
  background: var(--color-surface);
  color: var(--color-text-primary);
  
  &:focus {
    outline: none;
    border-color: rgba(187, 187, 187, 1);
    box-shadow: 0 0 0 2px rgba(187, 187, 187, 0.6);
  }
  
  &::placeholder {
    color: #999;
  }
  
  :root:not([data-theme='light']) & {
    background: var(--color-category-input-bg);
    color: #ffffff;
    -webkit-text-fill-color: #ffffff;
    caret-color: #ffffff;
  }

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 30px #f0f0f0 inset !important;
    box-shadow: 0 0 0 30px #f0f0f0 inset !important;
    -webkit-text-fill-color: #111111 !important;
  }
  :root:not([data-theme='light']) &:-webkit-autofill,
  :root:not([data-theme='light']) &:-webkit-autofill:hover,
  :root:not([data-theme='light']) &:-webkit-autofill:focus,
  :root:not([data-theme='light']) &:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 30px #2f2f2f inset !important;
    box-shadow: 0 0 0 30px #2f2f2f inset !important;
    -webkit-text-fill-color: #ffffff !important;
    color: #ffffff !important;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const Button = styled.button<{ primary?: boolean }>`
  flex: 1;
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  background: ${props => props.primary ? 'var(--color-accent-strong)' : 'var(--color-panel)'};
  color: ${props => props.primary ? 'white' : '#666'};
  
  /* 다크모드: 비활성 버튼 텍스트를 조금 더 밝게 */
  :root:not([data-theme='light']) & {
    color: ${props => props.primary ? 'white' : '#777777'};
  }
  
  &:hover {
    background: ${props => props.primary ? 'var(--color-accent)' : 'var(--color-border)'};
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(187, 187, 187, 0.6);
  }
  
  &:disabled {
    background: var(--color-save-button-disabled-bg);
    color: var(--color-save-button-disabled-text);
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  background: #bbb;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 1rem;
  cursor: pointer;
  flex: 1;
  transition: all 0.3s ease;
  
  &:hover {
    background: #999;
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(187, 187, 187, 0.6);
  }
  
  &:disabled {
    background: var(--color-save-button-disabled-bg);
    color: var(--color-save-button-disabled-text);
    cursor: not-allowed;
  }
`;

const StatusMessage = styled.div<{ isError?: boolean }>`
  color: var(--color-text-primary);
  font-size: 0.9rem;
  margin-top: 16px;
  padding: 12px;
  border-radius: 6px;
  background: var(--color-panel);
  border: 1px solid var(--color-border);
`;

const InfoText = styled.p`
  color: #666;
  font-size: 0.9rem;
  line-height: 1.4;
  margin-bottom: 16px;
  
  /* 다크모드: 조금 더 밝게 */
  :root:not([data-theme='light']) & {
    color: #777777;
  }
`;

const IdPasswordFindModal: React.FC<IdPasswordFindModalProps> = ({ 
  isOpen, 
  onClose 
}) => {
  const [activeTab, setActiveTab] = useState<'id' | 'password'>('id');
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage('');
    setIsError(false);
    setIsLoading(true);

    try {
      await sendPasswordResetEmail(auth, email);
      setMessage('비밀번호 재설정 이메일이 전송되었습니다. 이메일을 확인하여 새 비밀번호를 설정해주세요.');
      setIsError(false);
    } catch (error: any) {
      let errorMessage = '비밀번호 재설정 이메일 전송에 실패했습니다.';
      if (error.code === 'auth/user-not-found') {
        setMessage('해당 이메일로 등록된 계정을 찾을 수 없습니다. 이메일 주소를 확인하거나 회원가입을 진행해주세요.');
        setIsError(true);
      } else if (error.code === 'auth/invalid-email') {
        setMessage('유효하지 않은 이메일 주소입니다.');
        setIsError(true);
      } else if (error.code === 'auth/too-many-requests') {
        setMessage('요청이 너무 많습니다. 잠시 후 다시 시도해주세요.');
        setIsError(true);
      } else {
        setMessage(errorMessage);
        setIsError(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleIdFind = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage('');
    setIsError(false);
    setIsLoading(true);

    try {
      // Firebase Auth에서 이메일로 가입된 계정이 있는지 직접 확인
      const signInMethods = await fetchSignInMethodsForEmail(auth, email);
      
      if (signInMethods.length > 0) {
        setMessage('해당 이메일로 가입된 계정이 있습니다. 로그인 페이지에서 이메일 주소로 로그인하세요.');
        setIsError(false);
      } else {
        setMessage('해당 이메일로 가입된 계정이 없습니다. 이메일 주소를 확인하거나 회원가입을 진행해주세요.');
        setIsError(true);
      }
    } catch (error: any) {
      if (error.code === 'auth/invalid-email') {
        setMessage('유효하지 않은 이메일 주소입니다.');
        setIsError(true);
      } else if (error.code === 'auth/too-many-requests') {
        setMessage('요청이 너무 많습니다. 잠시 후 다시 시도해주세요.');
        setIsError(true);
      } else {
        setMessage('계정 찾기에 실패했습니다. 잠시 후 다시 시도해주세요.');
        setIsError(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setMessage('');
    setIsError(false);
    setIsLoading(false);
    setActiveTab('id');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <ModalOverlay />
      <ModalWrapper>
        <ModalContent>
          <Title>계정/비밀번호 찾기</Title>
          
          <TabContainer>
            <Tab 
              isActive={activeTab === 'id'} 
              onClick={() => setActiveTab('id')}
            >
              계정 찾기
            </Tab>
            <Tab 
              isActive={activeTab === 'password'} 
              onClick={() => setActiveTab('password')}
            >
              비밀번호 찾기
            </Tab>
          </TabContainer>
          
          {activeTab === 'id' ? (
            <>
              <InfoText>
                가입 시 사용한 이메일 주소를 입력하시면<br />
                해당 이메일로 가입된 계정이 있는지 확인해드립니다.
              </InfoText>
              
              <Form onSubmit={handleIdFind}>
                <InputGroup>
                  <Label>이메일 주소</Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="가입 시 사용한 이메일을 입력하세요"
                    required
                    disabled={isLoading}
                  />
                </InputGroup>
                
                <ButtonGroup>
                  <CancelButton data-variant="modal-secondary" type="button" onClick={handleClose} disabled={isLoading}>
                    취소
                  </CancelButton>
                  <Button primary type="submit" disabled={isLoading || !email.trim()}>
                    {isLoading ? '확인 중...' : '계정 찾기'}
                  </Button>
                </ButtonGroup>
              </Form>
            </>
          ) : (
            <>
              <InfoText>
                가입 시 사용한 이메일 주소를 입력하시면<br />
                비밀번호 재설정 링크를 보내드립니다.
              </InfoText>
              
              <Form onSubmit={handlePasswordReset}>
                <InputGroup>
                  <Label>이메일 주소</Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="가입 시 사용한 이메일을 입력하세요"
                    required
                    disabled={isLoading}
                  />
                </InputGroup>
                
                <ButtonGroup>
                  <CancelButton data-variant="modal-secondary" type="button" onClick={handleClose} disabled={isLoading}>
                    취소
                  </CancelButton>
                  <Button primary type="submit" disabled={isLoading || !email.trim()}>
                    {isLoading ? '전송 중...' : '비밀번호 재설정'}
                  </Button>
                </ButtonGroup>
              </Form>
            </>
          )}
          
          {message && (
            <StatusMessage isError={isError}>
              {message}
            </StatusMessage>
          )}
        </ModalContent>
      </ModalWrapper>
    </>
  );
};

export default IdPasswordFindModal; 